import React, { useState } from "react";
import { motion } from "framer-motion";
import { Icon } from "./icon";

const initialState = { name: "", email: "", company: "", message: "" };

/* The shared mail service in mercuri-app-api. `site` is a key, not an address:
 * the API maps it to CONTACT_EMAIL_BUSINESS server-side so the endpoint can't
 * be used to mail arbitrary recipients. */
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3000";
const CONTACT_ENDPOINT = `${API_BASE}/api/contact`;

const fieldClass =
  "w-full rounded-xl border border-white/12 bg-white/[0.05] px-4 py-3.5 text-white placeholder-white/30 " +
  "transition-all duration-300 focus:border-crimson-400 focus:bg-white/[0.09] focus:outline-none focus:ring-0";

const labelClass =
  "mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-white/50";

export const Contact = (props) => {
  const [{ name, email, company, message }, setState] = useState(initialState);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const handleChange = (e) => {
    const { name: field, value } = e.target;
    setState((prev) => ({ ...prev, [field]: value }));
  };

  const clearState = () => setState({ ...initialState });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");

    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, company, message, site: "business" }),
      });

      /* The API answers 400/500 with a JSON body rather than throwing, so a
       * non-ok response has to be checked explicitly — fetch only rejects on
       * network failure. */
      const body = await res.json().catch(() => null);

      if (!res.ok || !body || body.status !== true) {
        setStatus("error");
        return;
      }

      clearState();
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  const details = [
    { icon: "mapPin", label: "Office", value: props.data ? props.data.address : "—" },
    { icon: "phone", label: "Phone", value: props.data ? props.data.phone : "—" },
    { icon: "mail", label: "Email", value: props.data ? props.data.email : "—" },
    { icon: "clock", label: "Hours", value: props.data ? props.data.hours : "—" },
  ];

  const socials = [
    { icon: "linkedin", label: "LinkedIn", url: props.data ? props.data.linkedin : "/" },
    { icon: "x", label: "X", url: props.data ? props.data.twitter : "/" },
    { icon: "facebook", label: "Facebook", url: props.data ? props.data.facebook : "/" },
    { icon: "youtube", label: "YouTube", url: props.data ? props.data.youtube : "/" },
  ];

  return (
    <section
      id="contact"
      className="on-dark section relative overflow-hidden bg-navy-950 text-white"
    >
      <div
        className="absolute inset-0 bg-gradient-to-b from-navy-900 via-navy-950 to-navy-950"
        aria-hidden="true"
      />
      <div className="grid-bg-dark mask-fade absolute inset-0 opacity-50" aria-hidden="true" />
      <div
        className="absolute -right-32 top-0 h-[32rem] w-[32rem] rounded-full bg-crimson-600/20 blur-[140px]"
        aria-hidden="true"
      />

      <div className="container relative z-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-20">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <span className="eyebrow-dark">Get in touch</span>
            <h2 className="section-heading mt-6 text-white">
              Tell us what you're
              <span className="gradient-text-light"> trying to build</span>
            </h2>
            <p className="section-sub text-white/60">
              Send us a short brief and we'll come back within one business day with
              questions, a rough shape and next steps.
            </p>

            <form onSubmit={handleSubmit} className="mt-10 space-y-5">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="name" className={labelClass}>
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={name}
                    placeholder="Jane Cooper"
                    className={fieldClass}
                    required
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="email" className={labelClass}>
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    placeholder="jane@company.com"
                    className={fieldClass}
                    required
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="company" className={labelClass}>
                  Company <span className="normal-case tracking-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={company}
                  placeholder="Acme Inc."
                  className={fieldClass}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label htmlFor="message" className={labelClass}>
                  Project details
                </label>
                <textarea
                  name="message"
                  id="message"
                  rows="5"
                  value={message}
                  placeholder="What are you building, who is it for, and what's the timeline?"
                  className={`${fieldClass} resize-none`}
                  required
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col items-start gap-4 pt-2 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="btn-primary group w-full sm:w-auto"
                >
                  {status === "sending" ? "Sending…" : "Send message"}
                  <Icon
                    name="arrowRight"
                    className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                  />
                </button>

                {/* Submit feedback — the original form silently swallowed both outcomes */}
                <p aria-live="polite" className="text-sm">
                  {status === "sent" && (
                    <span className="flex items-center gap-2 text-emerald-400">
                      <Icon name="check" className="h-4 w-4" strokeWidth="2.5" />
                      Thanks — we'll be in touch shortly.
                    </span>
                  )}
                  {status === "error" && (
                    <span className="text-crimson-300">
                      Something went wrong. Email us directly instead.
                    </span>
                  )}
                  {status === "idle" && (
                    <span className="text-white/40">
                      We reply within one business day.
                    </span>
                  )}
                </p>
              </div>
            </form>
          </motion.div>

          {/* Details */}
          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm">
              <h3 className="text-lg font-bold text-white">Contact details</h3>

              <ul className="mt-8 space-y-7">
                {details.map((d) => (
                  <li key={d.label} className="group flex items-start gap-4">
                    <span className="icon-tile-dark h-11 w-11 transition-all duration-500 group-hover:bg-crimson-600 group-hover:text-white group-hover:ring-crimson-500">
                      <Icon name={d.icon} className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <span className="block text-xs font-semibold uppercase tracking-[0.12em] text-white/40">
                        {d.label}
                      </span>
                      <span className="mt-1 block break-words leading-relaxed text-white/85">
                        {d.value}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="hairline-dark my-8" />

              <h4 className="text-xs font-semibold uppercase tracking-[0.12em] text-white/40">
                Follow us
              </h4>
              <div className="mt-4 flex gap-3">
                {socials.map((social) => (
                  <a
                    key={social.label}
                    href={social.url}
                    aria-label={social.label}
                    className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/12 bg-white/[0.05] text-white/70 transition-all duration-300 hover:-translate-y-0.5 hover:border-crimson-500 hover:bg-crimson-600 hover:text-white"
                  >
                    <Icon name={social.icon} className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};
